import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@flows.cash/core'
import type { User } from '@flows.cash/types'

interface AuthState {
  user: User | null
  isLoading: boolean
  error: Error | null
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null,
  })

  useEffect(() => {
    // Get initial state from auth store
    const { user, isLoading, error } = useAuthStore.getState()
    setAuthState({
      user,
      isLoading,
      error: error ? new Error(error) : null,
    })

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const user = session.user as User
          setAuthState({
            user,
            isLoading: false,
            error: null,
          })
        } else if (event === 'SIGNED_OUT') {
          setAuthState({
            user: null,
            isLoading: false,
            error: null,
          })
        }
      }
    )

    // Subscribe to auth store changes
    const unsubscribeFromStore = useAuthStore.subscribe(
      (state) => {
        setAuthState({
          user: state.user,
          isLoading: state.isLoading,
          error: state.error ? new Error(state.error) : null,
        })
      }
    )

    return () => {
      subscription.unsubscribe()
      unsubscribeFromStore()
    }
  }, [])

  return authState
} 